import { AdminCertificatesSettingsExcludedVesselTypesFieldProps } from "../../../../models/admin-certificates-settings.models";
import { getAdminCertificatesSettingsVesselTypeOptions } from "../../../../utils/admin-certificates-settings.utils";
import {
  ADMIN_KPIS_FILTER_OPTION_LIST_MAX_HEIGHT,
  ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT,
} from "../../../../utils/admin-kpis.utils";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Virtuoso } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

const AdminCertificatesSettingsExcludedVesselTypesField = ({
  value,
  options,
  saving,
  onChange,
}: AdminCertificatesSettingsExcludedVesselTypesFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeOptions = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setMenuStyle(null);
  }, []);

  const availableOptions = useMemo(
    () => getAdminCertificatesSettingsVesselTypeOptions(options, value),
    [options, value],
  );
  const disabled = saving || availableOptions.length === 0;
  const selectedLabel = value.length === 0 ? "Choose an option" : `${value.length} selected`;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!normalizedSearchTerm) {
      return availableOptions;
    }

    return availableOptions.filter((option) => option.toLowerCase().includes(normalizedSearchTerm));
  }, [availableOptions, normalizedSearchTerm]);
  const selectedValues = useMemo(() => new Set(value), [value]);
  const showClear = value.length > 0 || normalizedSearchTerm.length > 0;
  const optionListHeight = Math.min(
    filteredOptions.length * ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT,
    ADMIN_KPIS_FILTER_OPTION_LIST_MAX_HEIGHT,
  );

  const updateMenuPosition = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    const menuHeight = optionListHeight + (showClear ? ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT : 0) + 2;
    const menuWidth = rect.width;
    const maxLeft = Math.max(8, window.innerWidth - menuWidth - 8);
    const left = Math.max(8, Math.min(rect.left, maxLeft));
    const belowTop = rect.bottom + 4;
    const aboveTop = Math.max(8, rect.top - menuHeight - 4);
    const shouldOpenAbove = window.innerHeight - belowTop < menuHeight && rect.top > menuHeight;

    setMenuStyle({
      left,
      top: shouldOpenAbove ? aboveTop : belowTop,
      width: menuWidth,
    });
  }, [optionListHeight, showClear]);

  useEffect(() => {
    if (disabled) {
      closeOptions();
    }
  }, [closeOptions, disabled]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuPosition();
    const handleViewportChange = () => updateMenuPosition();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      closeOptions();
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [closeOptions, isOpen]);

  return (
    <div
      className="grid min-w-0 gap-1 text-xs font-semibold uppercase"
      data-test="admin-certificates-settings-modal-field-EXCLUDED_VESSEL_TYPES"
    >
      <span className="truncate">EXCLUDE VESSEL TYPE</span>
      <div ref={containerRef} className="relative font-normal normal-case">
        <input
          ref={inputRef}
          type="search"
          value={searchTerm}
          aria-label="Search excluded vessel types"
          placeholder={selectedLabel}
          disabled={disabled}
          className="h-9 w-full border border-gray-300 bg-transparent px-3 pr-9 text-sm font-normal normal-case outline-none transition-colors placeholder:text-gray-500 focus:border-green disabled:opacity-50 dark:border-gray-700"
          onFocus={() => {
            if (disabled) {
              return;
            }

            updateMenuPosition();
            setIsOpen(true);
          }}
          onClick={() => {
            if (disabled) {
              return;
            }

            updateMenuPosition();
            setIsOpen(true);
          }}
          onChange={(event) => {
            if (disabled) {
              return;
            }

            setSearchTerm(event.target.value);
            updateMenuPosition();
            setIsOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeOptions();
            }
          }}
        />
        <button
          type="button"
          aria-label="Toggle excluded vessel type options"
          disabled={disabled}
          className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-[#4A5655] transition-colors hover:text-green disabled:opacity-50 dark:text-gray-200"
          onClick={() => {
            if (disabled) {
              return;
            }

            if (isOpen) {
              closeOptions();
              return;
            }

            setIsOpen(true);
            updateMenuPosition();
            inputRef.current?.focus();
          }}
        >
          <ChevronDownIcon className={twMerge("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
        </button>

        {isOpen &&
          menuStyle &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              style={menuStyle}
              className="fixed z-[70] overflow-hidden border border-gray-300 bg-white shadow-md dark:border-gray-700 dark:bg-gray-900"
            >
              {showClear && (
                <button
                  type="button"
                  className="flex h-8 w-full items-center gap-2 border-b border-gray-300 px-3 text-left text-xs font-semibold text-green hover:bg-[#E9F4F3] dark:border-gray-700 dark:hover:bg-gray-800"
                  onClick={() => {
                    onChange([]);
                    setSearchTerm("");
                    inputRef.current?.focus();
                  }}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Clear
                </button>
              )}
              {availableOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">No options</div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-500">No matching options</div>
              ) : (
                <Virtuoso
                  data={filteredOptions}
                  style={{ height: optionListHeight }}
                  computeItemKey={(_, option) => option}
                  fixedItemHeight={ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT}
                  itemContent={(_, option) => {
                    const isChecked = selectedValues.has(option);

                    return (
                      <label
                        className={twMerge(
                          "flex min-h-8 cursor-pointer items-start gap-2 px-3 py-1.5 text-xs hover:bg-[#E9F4F3] dark:hover:bg-gray-800",
                          isChecked && "bg-[#F1FAF8] text-green dark:bg-teal-950/40",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          className="mt-0.5 h-3.5 w-3.5 flex-none accent-green"
                          onChange={(event) => {
                            if (event.target.checked) {
                              onChange([...value, option]);
                              return;
                            }

                            onChange(value.filter((selectedOption) => selectedOption !== option));
                          }}
                        />
                        <span className="break-words">{option}</span>
                      </label>
                    );
                  }}
                />
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default AdminCertificatesSettingsExcludedVesselTypesField;
